import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Sparkles, X } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useShell } from '@/contexts/shell-context';
import { useIsMobile } from '@/hooks/use-mobile';
import { HARRY_AVATAR_SRC } from '@/components/icons';

export interface CoachChatSheetProps {
  hint?: string;
  userName?: string;
}

export function CoachChatSheet({
  hint = 'Ask about assessments, posture, or drill progressions for your active client.',
  userName = 'there',
}: CoachChatSheetProps) {
  const { coachOpen, closeCoach } = useShell();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [showWelcome, setShowWelcome] = useState(true);

  // The question goes to Harry Agent, which opens a new conversation and sends it there.
  const handleSend = () => {
    const question = draft.trim();
    if (!question) return;
    setShowWelcome(false);
    closeCoach();
    navigate('/agent', { state: { firstMessage: question } });
    setDraft('');
  };

  return (
    <Sheet open={coachOpen} onOpenChange={(open) => !open && closeCoach()}>
      <SheetContent
        hideCloseButton
        side={isMobile ? 'bottom' : 'right'}
        className={
          isMobile
            ? 'flex h-[min(85vh,560px)] flex-col gap-0 rounded-t-2xl border-0 p-0 sm:max-w-full'
            : 'flex w-full max-w-[400px] flex-col gap-0 border-0 p-0 sm:max-w-[400px]'
        }
      >
        <div className="flex items-center justify-between bg-gradient-to-br from-primary to-primary/80 px-4 py-3 text-primary-foreground">
          <span className="flex items-center gap-2 text-base font-semibold">
            <Sparkles className="h-4 w-4" aria-hidden />
            Harry
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
            aria-label="Close chat"
            onClick={closeCoach}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {showWelcome && (
          <section className="bg-gradient-to-b from-primary/90 to-primary/70 px-6 py-8 text-center text-primary-foreground">
            <span className="relative mx-auto mb-4 block h-[72px] w-[72px] overflow-hidden rounded-full border-[3px] border-white/35">
              <img src={HARRY_AVATAR_SRC} alt="" className="h-full w-full object-cover" />
            </span>
            <p className="text-sm opacity-90">Hello {userName}!</p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight">How can I help?</h2>
            <Button
              type="button"
              variant="secondary"
              className="mt-5 rounded-full bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              onClick={() => {
                setShowWelcome(false);
                document.getElementById('coach-chat-input')?.focus();
              }}
            >
              Use natural language
            </Button>
          </section>
        )}

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-background px-4 py-4">
          <p className="rounded-lg bg-muted/60 px-3 py-2 text-sm text-muted-foreground">{hint}</p>
          <p className="max-w-[90%] self-start rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm">
            Hi — I can help with SOP scoring, posture review, or drill progressions. What do you need?
          </p>
        </div>

        <div className="border-t bg-background p-3">
          <div className="flex gap-2">
            <Input
              id="coach-chat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Message Harry…"
              className="min-h-11 flex-1 rounded-full"
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            />
            <Button
              type="button"
              size="icon"
              className="h-11 w-11 shrink-0 rounded-full"
              aria-label="Send"
              onClick={handleSend}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-2 text-center text-[0.68rem] text-muted-foreground">
            Harry uses AI. Verify clinical decisions.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
